// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axiosInstance from '../../../../@core/api/axios'

export const getMails = createAsyncThunk('/emails', async params => {
  const response = await axiosInstance.get('emails', { params, showSuccessToast: false })
  return {
    params,
    data: response.data
  }
})

export const updateMails = createAsyncThunk(
  '/updateMails',
  async ({ emailAccountId, emailIds, currentFolder, dataToUpdate }, { dispatch, getState }) => {
    const response = await axiosInstance.post('/updateMails', { emailAccountId, emailIds, currentFolder, dataToUpdate }, { showSuccessToast: false })
    await dispatch(getMails(getState().appEmailSlice.params))
    return {
      emailIds,
      dataToUpdate,
      data: response.data
    }
  }
)

export const updateMailLabel = createAsyncThunk(
  'appEmail/updateMailLabel',
  async ({ emailIds, label }, { dispatch, getState }) => {
    const response = await axiosInstance.post('/apps/email/update-emails-label', { emailIds, label }, { showSuccessToast: false })
    await dispatch(getMails(getState().email.params))
    return response.data
  }
)

// AsyncThunk per selezionare l'email corrente
export const selectCurrentMail = createAsyncThunk('/email', async ({ emailAccountId, id, folder }) => {
  const response = await axiosInstance.get('/email', { params: { emailAccountId, emailId: id, folder }, showSuccessToast: false })
  return {
    data: response.data
  }
})

export const downloadAttachment = async ({ emailAccountId, attachmentId, emailId, folder }) => {
  const response = await axiosInstance.post('/emails/attachment/download', { emailAccountId, attachmentId, emailId, folder }, {
    showSuccessToast: false, responseType: 'blob'
  })
  return {
    data: response.data
  }
}
export const sendEmailFromAccount = async (data) => {
  const response = await axiosInstance.post('/email/send', data, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  return {
    data: response.data
  }
}

// Creazione dello slice Redux
export const appEmailSlice = createSlice({
  name: 'appEmail',
  initialState: {
    accounts: [],
    mails: [],
    params: {},
    emailsMeta: [],
    selectedMails: [],
    currentMail: null,
    selectedAccount: {},
    countMessages: 0,
    pagination: [],
    loadingStates: {
      getMails: false,
      updateMails: false,
      updateMailLabel: false,
      selectCurrentMail: false,
      downloadAttachment: false,
      sendEmailFromAccount: false
    },
    errorMessages: {}
  },
  reducers: {
    selectMail: (state, action) => {
      const selectedMails = state.selectedMails
      if (!selectedMails.includes(action.payload)) {
        selectedMails.push(action.payload)
      } else {
        selectedMails.splice(selectedMails.indexOf(action.payload), 1)
      }
      state.selectedMails = selectedMails
    },
    selectAllMail: (state, action) => {
      const selectAllMailsArr = []
      if (action.payload) {
        selectAllMailsArr.length = 0
        state.mails.forEach(mail => selectAllMailsArr.push(mail.id))
      } else {
        selectAllMailsArr.length = 0
      }
      state.selectedMails = selectAllMailsArr
    },
    resetSelectedMail: state => {
      state.selectedMails = []
    },
    updateSelectedAccount: (state, action) => {
      state.selectedAccount = action.payload
      state.pagination = []
      state.currentMail = null // Resetta l'email corrente se necessario
    },
    setCurrentMailToNull: state => {
      state.currentMail = null
    }
  },
  extraReducers: builder => {
    builder
      // Stato di caricamento per getMails
      .addCase(getMails.pending, state => {
        state.loadingStates.getMails = true
      })
      .addCase(getMails.fulfilled, (state, action) => {
        state.loadingStates.getMails = false
        let currMail = null
        if (state.currentMail !== null && state.currentMail !== undefined) {
          currMail = action.payload.data.mails.find(i => i.id === state.currentMail.id)
          if (currMail) {
            // Mantieni l'array di risposte dalla currentMail precedente
            currMail.replies = state.currentMail.replies || []
          }
        }
        state.accounts = action.payload.data.accounts
        state.currentMail = currMail
        state.params = action.payload.params
        state.mails = action.payload.data.mails
        state.emailsMeta = action.payload.data.folders

        if (state?.selectedAccount?.id !== action.payload.data?.selectedAccount?.id) {
          state.selectedAccount = action.payload.data.selectedAccount
        }

        state.countMessages = action.payload.data.countMessages
        if (!state.pagination.some(page => page.next === action.payload.data.nextPageToken)) {
          const newPage = {
            page: state.pagination.length + 1,
            previous: state.pagination.length > 0 ? state.pagination[state.pagination.length - 1].next : null,
            next: action.payload.data.nextPageToken
          }
          state.pagination.push(newPage)
        }
      })
      .addCase(getMails.rejected, (state, action) => {
        state.loadingStates.getMails = false
        state.errorMessages.getMails = action.error.message
      })

      // Stato di caricamento per updateMails
      .addCase(updateMails.pending, state => {
        state.loadingStates.updateMails = true
      })
      .addCase(updateMails.fulfilled, (state, action) => {
        state.loadingStates.updateMails = false
        function updateMailData(email) {
          Object.assign(email, action.payload.dataToUpdate)
        }
        state.mails.forEach(email => {
          if (action.payload.emailIds.includes(email.id)) {
            updateMailData(email)
          }
        })
      })
      .addCase(updateMails.rejected, (state, action) => {
        state.loadingStates.updateMails = false
        state.errorMessages.updateMails = action.error.message
      })

      // Stato di caricamento per selectCurrentMail
      .addCase(selectCurrentMail.pending, state => {
        state.loadingStates.selectCurrentMail = true
      })
      .addCase(selectCurrentMail.fulfilled, (state, action) => {
        state.loadingStates.selectCurrentMail = false
        state.currentMail = action.payload.data.email
      })
      .addCase(selectCurrentMail.rejected, (state, action) => {
        state.loadingStates.selectCurrentMail = false
        state.errorMessages.selectCurrentMail = action.error.message
      })
  }
})

// Esportazione delle azioni
export const { selectMail, selectAllMail, resetSelectedMail, updateSelectedAccount, setCurrentMailToNull } = appEmailSlice.actions

// Esportazione del reducer
export default appEmailSlice.reducer
